import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Save, ExternalLink } from 'lucide-react';

import ImageUploader from '../../components/admin/ImageUploader';
import { heroMedia } from '../../utils/heroMedia';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminHeroPage = () => {
  const [settings, setSettings] = useState({
    hero_video_url: '',
    hero_poster_url: '',
    logo_url: '',
    xceed_openair_url: '',
    openair_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const [mediaTarget, setMediaTarget] = useState('mobile');
  const videoField = mediaTarget === 'desktop' ? 'hero_desktop_video_url' : 'hero_video_url';
  const posterField = mediaTarget === 'desktop' ? 'hero_desktop_poster_url' : 'hero_poster_url';
  const preview = heroMedia(settings, mediaTarget);

  const uploadVideo = async (file) => {
    if (!file) return;
    if (!/\.(mp4|webm)$/i.test(file.name) || file.size > 100 * 1024 * 1024) {
      toast.error('Choisissez une vidéo MP4 ou WebM de maximum 100 Mo.');
      return;
    }
    setUploading(true);
    setProgress(0);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/upload-video`, form, {
        headers: { Authorization: `Basic ${localStorage.getItem('admin_auth')}` },
        onUploadProgress: ({ loaded, total }) => setProgress(total ? Math.round(loaded * 100 / total) : 0),
      });
      setSettings(current => ({ ...current, [videoField]: data.url }));
      toast.success('Vidéo importée. Vérifiez la prévisualisation puis cliquez sur Sauvegarder.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import impossible. Votre vidéo actuelle est conservée.');
    } finally { setUploading(false); }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      const media = heroMedia(response.data);
      const desktopMedia = heroMedia(response.data, 'desktop');
      setSettings({ ...response.data, hero_video_url: media.video, hero_poster_url: media.poster,
        hero_desktop_video_url: desktopMedia.video, hero_desktop_poster_url: desktopMedia.poster });
      setReady(true);
    } catch (error) {
      toast.error('Impossible de charger les réglages. Rechargez la page avant de modifier.');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings`, settings, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Paramètres sauvegardés!');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-hero-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Page d'accueil</h1>
          <p className="text-sm text-[#B9B2A8]">Modifiez la vidéo, le logo et les liens principaux</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={loading || uploading || imageUploading || !ready}
          className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Hero Video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Vidéo d'arrière-plan</h2>
          
          <div className="flex gap-3 mb-4" role="group" aria-label="Version de la vidéo">
            {['mobile', 'desktop'].map(target => (
              <Button key={target} type="button" aria-pressed={mediaTarget === target}
                disabled={uploading || imageUploading || loading}
                onClick={() => setMediaTarget(target)}
                className={mediaTarget === target ? 'bg-[#C9A84C] text-black' : 'bg-white/10 text-white'}>
                {target === 'mobile' ? 'Téléphone · verticale' : 'Ordinateur · grand écran'}
              </Button>
            ))}
          </div>
          <p className="text-xs text-[#B9B2A8] mb-4">Le site choisit automatiquement la vidéo : téléphone en dessous de 768 px, ordinateur et tablette à partir de 768 px.</p>
          <fieldset disabled={!ready || loading || uploading} className="space-y-4">
            <div>
              <label htmlFor="hero-video-upload" className="block text-sm text-[#F5F0E8] mb-2">Importer une vidéo depuis mon ordinateur</label>
              <input id="hero-video-upload" type="file" accept="video/mp4,video/webm,.mp4,.webm"
                onChange={event => { uploadVideo(event.target.files?.[0]); event.target.value = ''; }}
                className="block w-full text-sm text-[#B9B2A8]" />
              <p className="text-xs text-[#B9B2A8] mt-2" role="status">
                {uploading ? `Import en cours : ${progress} %` : 'MP4 H.264 recommandé · maximum 100 Mo. Une vidéo courte et légère charge plus vite.'}
              </p>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
                URL de la vidéo (MP4)
              </label>
              <Input
                value={settings[videoField] || ''}
                onChange={(e) => setSettings({ ...settings, [videoField]: e.target.value })}
                placeholder="https://example.com/video.mp4"
                className="bg-white/5 border-white/10 text-[#F5F0E8]"
              />
              <p className="text-xs text-[#B9B2A8] mt-1">
                Ou collez un lien direct vers un fichier MP4/WebM (pas une page YouTube ou Vimeo). Un champ vide utilise la vidéo par défaut.
              </p>
            </div>

            <ImageUploader
              value={settings[posterField]}
              onChange={url => setSettings(current => ({ ...current, [posterField]: url }))}
              onUploadingChange={setImageUploading}
              label="Image affichée avant la vidéo"
            />

            {preview.video && (
              <div className="mt-4">
                <p className="text-xs text-[#B9B2A8] mb-2">Prévisualisation:</p>
                <video
                  key={preview.video}
                  src={preview.video}
                  poster={preview.poster}
                  className="w-full max-w-md rounded-lg"
                  controls
                  muted
                  playsInline
                  onError={() => toast.error('Cette vidéo ne peut pas être lue. Utilisez un MP4 encodé en H.264 ou un WebM.')}
                />
              </div>
            )}
          </fieldset>
        </motion.div>

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Logo</h2>
          
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
              URL du logo (PNG transparent recommandé)
            </label>
            <Input
              value={settings.logo_url || ''}
              onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
              placeholder="https://example.com/logo.png"
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
            {settings.logo_url && (
              <div className="mt-4 p-4 bg-[#060606] rounded-lg inline-block">
                <img src={settings.logo_url} alt="Logo" className="h-16" />
              </div>
            )}
          </div>
        </motion.div>

        {/* Xceed Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Lien Tickets (Xceed)</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
                URL Xceed Open Air
              </label>
              <div className="flex gap-2">
                <Input
                  value={settings.xceed_openair_url || ''}
                  onChange={(e) => setSettings({ ...settings, xceed_openair_url: e.target.value })}
                  placeholder="https://xceed.me/..."
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
                {settings.xceed_openair_url && (
                  <a href={settings.xceed_openair_url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="icon" className="border-white/10">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Countdown Date */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Date du Countdown</h2>
          
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">
              Date et heure de l'Open Air
            </label>
            <Input
              type="datetime-local"
              value={settings.openair_date ? settings.openair_date.slice(0, 16) : ''}
              onChange={(e) => setSettings({ ...settings, openair_date: e.target.value })}
              className="bg-white/5 border-white/10 text-[#F5F0E8]"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminHeroPage;
